"use client";

import { c as _c } from "react/compiler-runtime";
import { cn } from "@/lib/utils";
import { Checkbox as MantineCheckbox, Skeleton, type CheckboxIconComponent, type CheckboxProps as MantineCheckboxProps, type CheckboxStylesNames } from "@mantine/core";
import { CheckboxMinusIcon, CheckboxTickIcon } from "./checkbox-icons";
import classes from "./checkbox.module.css";
const CheckboxIcon: CheckboxIconComponent = t0 => {
  const $ = _c(4);
  const {
    indeterminate,
    className
  } = t0;
  if (indeterminate) {
    let t1;
    if ($[0] !== className) {
      t1 = <CheckboxMinusIcon className={className} />;
      $[0] = className;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  let t1;
  if ($[2] !== className) {
    t1 = <CheckboxTickIcon className={className} />;
    $[2] = className;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
};
export type CheckboxProps = Omit<MantineCheckboxProps, "size" | "color" | "radius" | "error" | "onChange" | "classNames" | "icon"> & {
  /** Change handler — receives the next checked value */
  onCheckedChange?: (checked: boolean) => void;
  /** Renders the border and focus ring in red to signal validation failure */
  error?: boolean;
  /** Shows a skeleton shimmer instead of the checkbox */
  isLoading?: boolean;
  className?: string;
  classNames?: Partial<Record<CheckboxStylesNames, string>>;
};

/**
 * Design-system checkbox (Figma StreamHub UI Kit).
 * Built on Mantine `Checkbox` with styles in `checkbox.module.css`.
 */
export function Checkbox(t0) {
  const $ = _c(66);
  let checked;
  let className;
  let classNames;
  let defaultChecked;
  let description;
  let id;
  let indeterminate;
  let label;
  let onCheckedChange;
  let props;
  let t1;
  let t2;
  let t3;
  if ($[0] !== t0) {
    ({
      checked,
      defaultChecked,
      indeterminate,
      onCheckedChange,
      disabled: t1,
      error: t2,
      isLoading: t3,
      label,
      description,
      id,
      className,
      classNames,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = checked;
    $[2] = className;
    $[3] = classNames;
    $[4] = defaultChecked;
    $[5] = description;
    $[6] = id;
    $[7] = indeterminate;
    $[8] = label;
    $[9] = onCheckedChange;
    $[10] = props;
    $[11] = t1;
    $[12] = t2;
    $[13] = t3;
  } else {
    checked = $[1];
    className = $[2];
    classNames = $[3];
    defaultChecked = $[4];
    description = $[5];
    id = $[6];
    indeterminate = $[7];
    label = $[8];
    onCheckedChange = $[9];
    props = $[10];
    t1 = $[11];
    t2 = $[12];
    t3 = $[13];
  }
  const disabled = t1 === undefined ? false : t1;
  const error = t2 === undefined ? false : t2;
  const isLoading = t3 === undefined ? false : t3;
  if (isLoading) {
    let t4;
    if ($[14] !== className) {
      t4 = cn(classes.loading, className);
      $[14] = className;
      $[15] = t4;
    } else {
      t4 = $[15];
    }
    let t5;
    if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <Skeleton className={classes.loadingBox} />;
      $[16] = t5;
    } else {
      t5 = $[16];
    }
    let t6;
    if ($[17] !== label) {
      t6 = label && <Skeleton className={classes.loadingLabel} />;
      $[17] = label;
      $[18] = t6;
    } else {
      t6 = $[18];
    }
    let t7;
    if ($[19] !== t4 || $[20] !== t6) {
      t7 = <div className={t4}>{t5}{t6}</div>;
      $[19] = t4;
      $[20] = t6;
      $[21] = t7;
    } else {
      t7 = $[21];
    }
    return t7;
  }
  let t4;
  if ($[22] !== onCheckedChange) {
    t4 = event => onCheckedChange?.(event.currentTarget.checked);
    $[22] = onCheckedChange;
    $[23] = t4;
  } else {
    t4 = $[23];
  }
  const t5 = disabled && classes.rootDisabled;
  const t6 = classNames?.root;
  let t7;
  if ($[24] !== t5 || $[25] !== t6) {
    t7 = cn(classes.root, t5, t6);
    $[24] = t5;
    $[25] = t6;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  const t8 = classNames?.body;
  let t9;
  if ($[27] !== t8) {
    t9 = cn(classes.body, t8);
    $[27] = t8;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  const t10 = error && classes.innerError;
  const t11 = classNames?.inner;
  let t12;
  if ($[29] !== t10 || $[30] !== t11) {
    t12 = cn(classes.inner, t10, t11);
    $[29] = t10;
    $[30] = t11;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  const t13 = classNames?.input;
  let t14;
  if ($[32] !== t13) {
    t14 = cn(classes.input, t13);
    $[32] = t13;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  const t15 = classNames?.icon;
  let t16;
  if ($[34] !== t15) {
    t16 = cn(classes.icon, t15);
    $[34] = t15;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  const t17 = classNames?.labelWrapper;
  let t18;
  if ($[36] !== t17) {
    t18 = cn(classes.labelWrapper, t17);
    $[36] = t17;
    $[37] = t18;
  } else {
    t18 = $[37];
  }
  const t19 = classNames?.label;
  let t20;
  if ($[38] !== t19) {
    t20 = cn(classes.label, t19);
    $[38] = t19;
    $[39] = t20;
  } else {
    t20 = $[39];
  }
  const t21 = classNames?.description;
  let t22;
  if ($[40] !== t21) {
    t22 = cn(classes.description, t21);
    $[40] = t21;
    $[41] = t22;
  } else {
    t22 = $[41];
  }
  const t23 = classNames?.error;
  let t24;
  if ($[42] !== t23) {
    t24 = cn(classes.error, t23);
    $[42] = t23;
    $[43] = t24;
  } else {
    t24 = $[43];
  }
  let t25;
  if ($[44] !== t12 || $[45] !== t14 || $[46] !== t16 || $[47] !== t18 || $[48] !== t20 || $[49] !== t22 || $[50] !== t24 || $[51] !== t7 || $[52] !== t9) {
    t25 = {
      root: t7,
      body: t9,
      inner: t12,
      input: t14,
      icon: t16,
      labelWrapper: t18,
      label: t20,
      description: t22,
      error: t24
    };
    $[44] = t12;
    $[45] = t14;
    $[46] = t16;
    $[47] = t18;
    $[48] = t20;
    $[49] = t22;
    $[50] = t24;
    $[51] = t7;
    $[52] = t9;
    $[53] = t25;
  } else {
    t25 = $[53];
  }
  let t26;
  if ($[54] !== checked || $[55] !== className || $[56] !== defaultChecked || $[57] !== description || $[58] !== disabled || $[59] !== id || $[60] !== indeterminate || $[61] !== label || $[62] !== props || $[63] !== t25 || $[64] !== t4) {
    t26 = <MantineCheckbox data-slot="checkbox" unstyled={true} id={id} checked={checked} defaultChecked={defaultChecked} indeterminate={indeterminate} disabled={disabled} label={label} description={description} icon={CheckboxIcon} withErrorStyles={false} onChange={t4} className={className} classNames={t25} {...props} />;
    $[54] = checked;
    $[55] = className;
    $[56] = defaultChecked;
    $[57] = description;
    $[58] = disabled;
    $[59] = id;
    $[60] = indeterminate;
    $[61] = label;
    $[62] = props;
    $[63] = t25;
    $[64] = t4;
    $[65] = t26;
  } else {
    t26 = $[65];
  }
  return t26;
}